import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { User, UserRole } from '../../../types';
import { isSubmitter, rolesOf } from '../../../utils/roles';
import { roleLabel } from '../../../utils/roleLabels';
import styled from '@emotion/styled';
import { Chip, IconButton, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Typography } from '../../../components/Typography/Typography';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { StatusBadge } from '../../../components/StatusBadge/StatusBadge';
import { ReactComponent as MoreIcon } from '../../../assets/icons/more-vertical.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import ActionsMenu from '../components/ActionsMenu';
import { useSetAtom } from 'jotai';
import {
  selectedUserForEditAtom,
  selectedUserForDeleteAtom,
  selectedUserForResetAtom,
} from '../../../state/UIAccountantState';

const CellWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const columnHelper = createColumnHelper<User>();

export const ActionCell = (props: CellContext<User, unknown>) => {
  const { colors } = useTheme();
  const user = props.row.original;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const setSelectedUserForEdit = useSetAtom(selectedUserForEditAtom);
  const setSelectedUserForDelete = useSetAtom(selectedUserForDeleteAtom);
  const setSelectedUserForReset = useSetAtom(selectedUserForResetAtom);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditUserProfileOpen = useCallback(() => {
    setSelectedUserForEdit(user);
    handleClose();
  }, [user, setSelectedUserForEdit]);

  const handleResetUserProfileOpen = useCallback(() => {
    setSelectedUserForReset(user);
    handleClose();
  }, [user, setSelectedUserForReset]);

  const handleDeleteUserProfileOpen = useCallback(() => {
    setSelectedUserForDelete(user);
    handleClose();
  }, [user, setSelectedUserForDelete]);

  const isOperatingPartner = useMemo(() => {
    return isSubmitter(user);
  }, [user]);

  return (
    <CellWrapper style={{ justifyContent: 'end', paddingRight: '12px' }}>
      <IconButton
        onClick={handleEditUserProfileOpen}
        size='small'
        aria-label='Edit profile'
        sx={{ svg: { g: { path: { fill: colors.icon.accent } } } }}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        onClick={handleClick}
        size='small'
        sx={{ svg: { g: { path: { fill: colors.icon.accent } } } }}
      >
        <MoreIcon />
      </IconButton>
      <ActionsMenu
        anchorEl={anchorEl}
        canResetUserProfile={isOperatingPartner}
        handleClose={handleClose}
        handleResetUserProfileOpen={handleResetUserProfileOpen}
        handleDeleteUserProfileOpen={handleDeleteUserProfileOpen}
      />
    </CellWrapper>
  );
};

export const useUsersTableColumns = () => {
  const { colors } = useTheme();

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.caption}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='ID' />,
        meta: {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: { borderRight: `none`, paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <AvatarCell
            name={info.getValue()}
            avatarBoxStyle={{
              background: colors.surfaceIndicator.needsReview,
            }}
          />
        ),
        header: () => <TableHeaderCell text='User' />,
        filterFn: (row, id, filterValue) => {
          const isUserNameMatched = (row.original.name ?? '')
            .toLowerCase()
            .includes(filterValue.toLowerCase());
          const isEmailMatched = (row.original.email ?? '')
            .toLowerCase()
            .includes(filterValue.toLowerCase());
          return isUserNameMatched || isEmailMatched;
        },
        meta: {
          width: '250px',
          maxWidth: '250px',
          minWidth: '250px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('isActive', {
        cell: (info) => <StatusBadge isActive={info.getValue() !== false} />,
        header: () => <TableHeaderCell text='Status' />,
        filterFn: (row, id, filterValue) => {
          if (filterValue === undefined || filterValue === null) return true;
          const isActive = row.original.isActive !== false;
          return isActive === filterValue;
        },
        meta: {
          width: '150px',
          maxWidth: '150px',
          minWidth: '150px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('roles', {
        // Multi-role: render one chip per role the user holds (source of truth is `roles`). The role
        // filter matches membership (see filterFn), so a Manager-Submitter shows up under both
        // "Manager" and "Submitter".
        cell: (info) => {
          const userRoles = rolesOf(info.row.original);
          if (userRoles.length === 0) return null;
          return (
            <CellWrapper>
              {userRoles.map((role) => (
                <Chip
                  key={role}
                  style={{
                    backgroundColor: colors.surfaceStatus.noAction,
                    minWidth: '66px',
                    height: '28px',
                  }}
                  label={
                    <Typography variant='body' color={colors.text.main}>
                      {roleLabel(role)}
                    </Typography>
                  }
                />
              ))}
            </CellWrapper>
          );
        },
        filterFn: (row, _id, filterValue) =>
          !filterValue || rolesOf(row.original).includes(filterValue as UserRole),
        header: () => <TableHeaderCell text='User Type' />,
        meta: {
          width: '35%',
          maxWidth: '35%',
          minWidth: '35%',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('email', {
        cell: (info) => (
          <Typography variant='h5' color={colors.text.main}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='Contact' />,
        meta: {
          width: '50%',
          maxWidth: '50%',
          minWidth: '50%',
          tdStyles: { borderRight: `none` },
          thStyles: { borderRight: `none` },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => <ActionCell {...info} />,
        header: () => <TableHeaderCell text='' />,
        meta: {
          width: '84px',
          minWidth: '84px',
          maxWidth: '84px',
          tdStyles: { paddingLeft: '0', borderLeft: `none` },
          thStyles: { borderLeft: `none` },
        },
      }),
    ],
    [colors]
  );

  return columns;
};
